import React from 'react';

// Reusable modal to display a single submission (styled to match SubmissionDetails)
interface Props {
  isOpen: boolean;
  submission: any | null;
  loading?: boolean;
  onClose: () => void;
}

const formatFieldName = (fieldName: string): string =>
  fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();

const renderFieldValue = (value: any): React.ReactNode => {
  if (value === null || value === undefined || value === '') return 'Not provided';

  if (Array.isArray(value)) {
    if (value.length === 0) return 'No entries';
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {value.map((item, idx) => (
          <div key={idx} style={{ padding: 10, background: '#fff', borderRadius: 8, border: '1px solid #e9ecef' }}>
            {typeof item === 'object' ? (
              Object.entries(item).map(([k, v]) => (
                <div key={k} style={{ marginBottom: 6 }}>
                  <strong>{formatFieldName(k)}: </strong>
                  <span>{renderFieldValue(v)}</span>
                </div>
              ))
            ) : (
              String(item)
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value);
    // marriage / family style nested objects: render sections
    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {keys.map(k => {
          const v = (value as any)[k];
          if (typeof v === 'object') {
            return (
              <div key={k} style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e6eef8' }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{formatFieldName(k)}</div>
                {Object.entries(v).map(([fk, fv]) => (
                  <div key={fk} style={{ marginBottom: 6 }}>
                    <strong>{formatFieldName(fk)}: </strong><span>{renderFieldValue(fv)}</span>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div key={k} style={{ marginBottom: 6 }}>
              <strong>{formatFieldName(k)}: </strong><span>{renderFieldValue(v)}</span>
            </div>
          );
        })}
      </div>
    );
  }

  return String(value);
};

export default function SubmissionModal({ isOpen, submission, loading, onClose }: Props) {
  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
      <div style={{ background: 'white', width: '92%', maxWidth: 1000, maxHeight: '86%', overflow: 'auto', borderRadius: 10, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Submission Details</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {loading && <div style={{ padding: 20 }}>Loading...</div>}

        {!loading && !submission && <div style={{ padding: 20 }}>No submission selected.</div>}

        {!loading && submission && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div style={{ padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Client</div>
                <div style={{ fontWeight: 700 }}>{submission.client_name || 'Unnamed'}</div>
                <div style={{ color: '#6b7280', marginTop: 6 }}>
                  {submission.client_email || 'No email'} • {submission.client_phone || 'No phone'}
                </div>
              </div>

              <div style={{ padding: 12, background: '#fff', borderRadius: 8, border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: 14, color: '#6b7280' }}>Form</div>
                <div style={{ fontWeight: 700 }}>{submission.intake_forms?.title || submission.form_id || 'Intake Form'}</div>
                <div style={{ color: '#6b7280', marginTop: 6 }}>
                  Submitted: {new Date(submission.submitted_at || submission.submittedAt || submission.created_at || Date.now()).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Form data / case_details */}
            {submission.case_details ? (
              <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e6eef8' }}>
                <h4 style={{ marginTop: 0 }}>Form Data</h4>
                <div style={{ display: 'grid', gap: 12 }}>
                  {Object.entries(submission.case_details).map(([key, value]) => {
                    // Skip empty scalars and handle nested objects
                    if ((typeof value === 'string' && value.trim() === '') || value === null || value === undefined) return null;
                    return (
                      <div key={key} style={{ padding: 10, background: 'white', borderRadius: 8, border: '1px solid #e9ecef' }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{formatFieldName(key)}</div>
                        <div style={{ color: '#374151' }}>{renderFieldValue(value)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ padding: 12, background: 'white', borderRadius: 8, border: '1px solid #e9ecef' }}>
                No form data attached to this submission.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}